import { type ReactNode, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Skeleton,
  formatDate,
  useToast,
} from '@aps/ui';
import {
  type ConsignmentResponse,
  type ConsignmentStatusResult,
  type OrderStatusCode,
} from '@aps/contracts';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { QueueError } from './WorkQueueScreen.js';

/**
 * Xitoy partiyalari.
 *
 * Partiya = bitta mashina, bitta invoys, bitta bojxona rasmiylashtiruvi
 * (`schema.prisma`). Ekranning butun qiymati — **butun partiya
 * statusini bir bosishda ko'chirish**: usiz operator 40 ta buyurtmani
 * qo'lda ochishi kerak bo'lardi.
 *
 * Natija JIMGINA qabul qilinmaydi: nechta jo'natma ko'chdi, nechtasi
 * ko'chmadi va NEGA — hammasi ko'rsatiladi. "Bajarildi" deb yozib
 * qo'yish, aslida yarmi ko'chmagan holatda, eng yomon variant.
 */
export function ConsignmentsScreen({
  onOpenOrder,
}: {
  onOpenOrder: (orderId: string) => void;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const [result, setResult] = useState<ConsignmentStatusResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  /** Ichi ochilgan partiya — ko'chirishdan mustaqil. */
  const [shownId, setShownId] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['consignments'], queryFn: api.consignments });
  const transitions = useQuery({
    queryKey: ['status-transitions'],
    queryFn: api.statusTransitions,
  });

  const move = useMutation({
    mutationFn: (input: { id: string; toCode: OrderStatusCode }) =>
      api.changeConsignmentStatus(input.id, { toCode: input.toCode }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
      void client.invalidateQueries({ queryKey: ['consignments'] });
    },
    onError: (cause: unknown) => {
      setResult(null);
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  const header = (
    <header className="adm-page__header">
      <h1 className="adm-page__title">{t.navConsignments}</h1>
      <span className="aps-muted">{t.consignmentsCount(list.data?.length ?? 0)}</span>
    </header>
  );

  if (list.isPending) {
    return (
      <section className="adm-page">
        {header}
        <Skeleton height="260px" label={t.loading} />
      </section>
    );
  }

  if (list.isError) {
    return (
      <section className="adm-page">
        {header}
        <QueueError
          message={list.error instanceof ApiError ? list.error.text(locale) : t.errorTitle}
          retryLabel={t.retry}
          onRetry={() => void list.refetch()}
        />
      </section>
    );
  }

  const columns: DataTableColumn<ConsignmentResponse>[] = [
    {
      key: 'invoice',
      header: t.consignmentInvoice,
      // Invoys BOSILADI — ichidagi jo'natmalar ochiladi.
      render: (row) => (
        <button
          type="button"
          className="adm-link"
          onClick={() => {
            setShownId(row.id === shownId ? null : row.id);
          }}
        >
          {row.invoiceNo}
        </button>
      ),
    },
    {
      key: 'supplier',
      header: t.consignmentSupplier,
      render: (row) => row.supplierName ?? '—',
    },
    {
      key: 'status',
      header: t.colStatus,
      render: (row) => <span className="adm-badge">{row.statusLabelUz}</span>,
    },
    {
      key: 'shipments',
      header: t.consignmentShipments,
      numeric: true,
      render: (row) => String(row.shipmentCount),
    },
    {
      key: 'eta',
      header: t.colEta,
      render: (row) => (row.etaTo === null ? t.etaUnset : formatDate(row.etaTo, locale)),
    },
    {
      key: 'move',
      header: t.consignmentMove,
      render: (row) => {
        /*
         * Maqsad statuslar SERVERDAN kelgan jadvaldan olinadi —
         * klientda graf yozilmaydi (CLAUDE.md 5-taqiq). Bu yerda
         * faqat FILTR: joriy statusdan chiqadigan qatorlar.
         */
        const targets = (transitions.data ?? []).filter((item) => item.fromCode === row.statusCode);
        if (targets.length === 0) return <span className="aps-muted">{t.consignmentEnd}</span>;

        return (
          <div className="adm-filters__chips">
            {targets.map((target) => (
              <Button
                key={target.toCode}
                variant="ghost"
                size="sm"
                disabled={move.isPending}
                onClick={() => {
                  setOpenId(row.id);
                  move.mutate({ id: row.id, toCode: target.toCode });
                }}
              >
                {locale === 'RU' ? (target.toLabelRu ?? target.toLabelUz) : target.toLabelUz}
              </Button>
            ))}
          </div>
        );
      },
    },
  ];

  return (
    <section className="adm-page">
      {header}
      <p className="aps-field__hint">{t.consignmentsHint}</p>

      {error !== null && (
        <p className="aps-error" role="alert">
          {error}
        </p>
      )}

      {/*
        Ko'chirish natijasi — MAJBURIY. Partiyada 40 ta jo'natma
        bo'lishi mumkin va ularning bir qismi boshqa statusda turgan
        bo'lsa ko'chmaydi. Buni aytmaslik "hammasi ko'chdi" degan
        yolg'on taassurot berardi.
      */}
      {result !== null && openId !== null && (
        <section className="adm-panel" data-tone={result.skipped.length > 0 ? 'warn' : undefined}>
          <h2 className="adm-panel__title">{t.consignmentResult}</h2>
          <div className="adm-panel__body">
            <p>{t.consignmentMoved(result.movedCount, result.notifiedCount)}</p>
            {result.skipped.length > 0 && (
              <>
                <p className="aps-muted">{t.consignmentSkipped(result.skipped.length)}</p>
                <ul className="adm-list">
                  {result.skipped.map((item) => (
                    <li key={item.shipmentId}>
                      {item.fromCode} — {t.consignmentSkipReason(item.reason)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </section>
      )}

      {/*
        Partiya ochish — demodagi "Partiya ochish".
        Server endpointi va ta'minotchilar ro'yxati bor edi, forma
        yo'q edi: yangi yuk kelganda operator uni tizimga kirita
        olmasdi.
      */}
      <NewConsignment />

      <DataTable
        caption={t.navConsignments}
        columns={columns}
        rows={list.data}
        rowKey={(row) => row.id}
        empty={<EmptyState title={t.consignmentsEmpty} hint={t.consignmentsEmptyHint} />}
      />

      {/*
        Tanlangan partiya ICHIDAGI jo'natmalar.
        Butun partiyani bir bosishda ko'chirish — orqaga qaytarib
        bo'lmaydigan amal. Operator undan oldin ichida nima
        borligini ko'rishi kerak; ilgari ro'yxatda faqat SANOQ
        bor edi.
      */}
      {shownId !== null && <ConsignmentShipments id={shownId} onOpenOrder={onOpenOrder} />}
    </section>
  );
}

/**
 * Yangi partiya ochish.
 *
 * Invoys raqami MAJBURIY va u takrorlanmaydi — server buni
 * tekshiradi va 409 qaytaradi. Qolgan maydonlar keyin
 * to'ldiriladi: yuk yo'lda ekan bojxona raqami hali yo'q.
 */
function NewConsignment(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();
  const invoiceId = useId();
  const supplierId = useId();

  const [invoiceNo, setInvoiceNo] = useState('');
  const [supplier, setSupplier] = useState('');

  const suppliers = useQuery({ queryKey: ['suppliers'], queryFn: api.suppliers });

  const create = useMutation({
    mutationFn: () =>
      api.createConsignment({
        invoiceNo: invoiceNo.trim(),
        ...(supplier === '' ? {} : { supplierId: supplier }),
      }),
    onSuccess: () => {
      setInvoiceNo('');
      setSupplier('');
      void client.invalidateQueries({ queryKey: ['consignments'] });
      toast.show(t.consignmentCreated, 'success');
    },
    onError: (cause: unknown) => {
      toast.show(cause instanceof ApiError ? cause.text(locale) : t.errorTitle, 'danger');
    },
  });

  return (
    <section className="adm-panel">
      <h2 className="adm-panel__title">{t.consignmentNew}</h2>
      <div className="adm-panel__body adm-cons-new">
        <div className="aps-field">
          <label className="aps-field__label" htmlFor={invoiceId}>
            {t.consignmentInvoice}
          </label>
          <input
            id={invoiceId}
            className="aps-field__input"
            value={invoiceNo}
            onChange={(event) => {
              setInvoiceNo(event.target.value);
            }}
          />
        </div>

        <div className="aps-field">
          <label className="aps-field__label" htmlFor={supplierId}>
            {t.consignmentSupplier}
          </label>
          <select
            id={supplierId}
            className="aps-field__input"
            value={supplier}
            onChange={(event) => {
              setSupplier(event.target.value);
            }}
          >
            <option value="">{t.consignmentSupplierNone}</option>
            {(suppliers.data ?? []).map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </div>

        <Button
          disabled={invoiceNo.trim() === '' || create.isPending}
          onClick={() => {
            create.mutate();
          }}
        >
          {t.consignmentCreate}
        </Button>
      </div>
    </section>
  );
}

/** Partiya ichidagi jo'natmalar — bitta so'rovda keladi. */
function ConsignmentShipments({
  id,
  onOpenOrder,
}: {
  id: string;
  onOpenOrder: (orderId: string) => void;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);

  const detail = useQuery({
    queryKey: ['consignment', id],
    queryFn: () => api.consignment(id),
  });

  if (detail.isPending) return <Skeleton height="120px" label={t.loading} />;
  if (detail.isError) return <p className="aps-error">{t.errorTitle}</p>;

  return (
    <section className="adm-panel">
      <h2 className="adm-panel__title">
        {detail.data.invoiceNo} · {t.consignmentShipmentCount(detail.data.shipments.length)}
      </h2>
      <div className="adm-panel__body">
        {detail.data.shipments.length === 0 ? (
          <p className="aps-muted">{t.consignmentNoShipments}</p>
        ) : (
          <ul className="adm-list">
            {detail.data.shipments.map((shipment) => (
              <li className="adm-cons-row" key={shipment.shipmentId}>
                <button
                  type="button"
                  className="adm-link"
                  onClick={() => {
                    onOpenOrder(shipment.orderId);
                  }}
                >
                  {shipment.orderNumber}
                </button>
                <span className="adm-cons-row__who">
                  {shipment.customerName}
                  {shipment.regionNameUz === null ? '' : ` · ${shipment.regionNameUz}`}
                </span>
                <span className="aps-muted">{shipment.productNames.join(', ')}</span>
                {/*
                  Jo'natmaning O'Z statusi ko'rsatiladi: partiyadagi
                  hammasi bir xil statusda bo'lishi SHART EMAS va
                  ko'chmay qoladiganini oldindan ko'rish kerak.
                */}
                <span className="adm-badge">
                  {locale === 'RU'
                    ? (shipment.statusLabelRu ?? shipment.statusLabelUz)
                    : shipment.statusLabelUz}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
